import type { Lead } from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';

export { splitLeadDisplayName } from '@up2stay/up2promo-commons';

/**
 * Escape text for VCF 3.0: backslash, semicolon, comma, colon, and newlines must be escaped
 * so the vCard is well-formed (RFC 2426).
 */
export function escapeVcfText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/:/g, '\\:')
    .replace(/\r\n|\r|\n/g, '\\n');
}

function buildLeadNote(lead: Lead): string | undefined {
  const parts: string[] = [];
  const message = lead.message?.trim();
  if (message) parts.push(message);
  const description = lead.description?.trim();
  if (description) parts.push(description);
  return parts.length > 0 ? parts.join('\n\n') : undefined;
}

/** True when the lead has enough contact data for a useful vCard. */
export function leadHasVcfContent(lead: Lead): boolean {
  if (lead.firstName?.trim()) return true;
  if (lead.lastName?.trim()) return true;
  if (lead.email?.trim()) return true;
  if (lead.phone?.trim()) return true;
  if (lead.companyName?.trim()) return true;
  if (lead.jobTitle?.trim()) return true;
  if (lead.address?.trim()) return true;
  if (lead.message?.trim()) return true;
  if (lead.description?.trim()) return true;
  return false;
}

/**
 * Build a VCF 3.0 contact card from lead fields (name, org, title, email, tel, adr, url, note).
 */
export function buildVcfFromLead(lead: Lead): string {
  const firstName = lead.firstName?.trim() ?? '';
  const lastName = lead.lastName?.trim() ?? '';
  const hasPersonName = Boolean(firstName || lastName);
  const displayName =
    formatLeadDisplayName(lead.firstName, lead.lastName) ||
    lead.email?.trim() ||
    'Contact';
  const familyName = hasPersonName ? lastName : '';
  const givenName = hasPersonName ? firstName : displayName;

  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${[escapeVcfText(familyName), escapeVcfText(givenName), '', '', ''].join(
      ';',
    )}`,
    `FN:${escapeVcfText(displayName)}`,
  ];

  if (lead.companyName?.trim()) {
    lines.push(`ORG:${escapeVcfText(lead.companyName.trim())}`);
  }

  if (lead.jobTitle?.trim()) {
    lines.push(`TITLE:${escapeVcfText(lead.jobTitle.trim())}`);
  }

  if (lead.email?.trim()) {
    lines.push(`EMAIL;TYPE=INTERNET,WORK:${escapeVcfText(lead.email.trim())}`);
  }

  if (lead.phone?.trim()) {
    lines.push(`TEL;TYPE=WORK,VOICE:${escapeVcfText(lead.phone.trim())}`);
  }

  if (lead.address?.trim()) {
    lines.push(`ADR;TYPE=WORK:;;${escapeVcfText(lead.address.trim())};;;;`);
  }

  const note = buildLeadNote(lead);
  if (note) {
    lines.push(`NOTE:${escapeVcfText(note)}`);
  }

  lines.push('END:VCARD');
  return lines.join('\r\n');
}

/** `lead-{sanitized-name}.vcf` */
export function leadVcfFilename(lead: Lead): string {
  const base =
    (
      formatLeadDisplayName(lead.firstName, lead.lastName) ||
      lead.email?.trim() ||
      'contact'
    )
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'contact';
  return `lead-${base}.vcf`;
}
